/**
 * Video Manifest Types
 *
 * Defines the structure for multi-slide video playback.
 * The manifest describes the sequence of slides, their durations,
 * transitions, and audio configuration.
 *
 * ARCHITECTURE:
 * - Each slide is rendered via GsapSlidePreviewEnhanced (iframe isolation)
 * - React orchestrates playback, transitions, and audio
 * - GSAP runs entirely inside iframes (no global state)
 *
 * AUDIO TIERS:
 * 1. Slide Audio - Per-slide narration (defines slide duration)
 * 2. Background Music - Video-level ambient audio (future)
 */

/**
 * Transition types available between slides
 * Matches TransitionEngine.js implementation
 */
export type TransitionType =
  // Essential
  | 'crossfade'     // Simultaneous overlapping fade
  | 'cut'           // Instant hard cut (no animation)
  | 'fade-black'    // Fade to/from black
  // Content-Aware
  | 'content-exit'  // Content-aware exit animations
  | 'stagger-cascade' // Cascade exit animation
  | 'stagger-random'  // Random stagger exit
  // Directional
  | 'slide-left'    // Slide horizontally left
  | 'slide-right'   // Slide horizontally right
  | 'slide-up'      // Slide vertically up
  | 'slide-down'    // Slide vertically down
  // Reveal
  | 'wipe-left'     // Wipe from right to left
  | 'wipe-right'    // Wipe from left to right
  | 'circle-reveal' // Circular reveal from center
  // Emphasis
  | 'zoom-in'       // Zoom into next slide
  | 'zoom-out'      // Zoom out from current
  | 'blur-fade'     // Blur + fade combination
  // Cinematic (v2 — browser-rendered in export by the same TransitionEngine)
  | 'flash-white'   // Light floods the frame, cut hides under the peak
  | 'flash-accent'  // Same envelope in a brand color (TransitionConfig.color)
  | 'whip-left'     // Violent lateral fling with smear
  | 'whip-right'
  | 'glint-sweep'   // Diagonal light strip races across, swap under its center
  | 'letterbox-pinch' // Cinema bars slam shut, cut inside, release
  | 'iris-close'    // Iris collapses onto a backing color, blooms open on next
  | 'slats-wipe'    // Staggered ink bars cover and peel (color = bar color)
  | 'glitch-cut'    // Stepped jitter + RGB ghosts straddling the cut
  | 'film-dissolve' // True dissolve with a grain pulse (time passing)
  | 'wipe-up'
  | 'wipe-down'
  // Legacy (for backwards compatibility)
  | 'none'          // Alias for cut
  | 'fade';         // Alias for crossfade

/**
 * Transition category for UI grouping
 */
export type TransitionCategory = 'essential' | 'content-aware' | 'directional' | 'reveal' | 'emphasis' | 'cinematic';

/**
 * Transition option for UI display
 */
export interface TransitionOption {
  type: TransitionType;
  label: string;
  emoji: string;
  category: TransitionCategory;
  description?: string;
}

/**
 * Available transition options for the UI selector
 * Organized by category for easy selection
 */
export const TRANSITION_OPTIONS: TransitionOption[] = [
  // Essential
  { type: 'crossfade', label: 'Crossfade', emoji: '✦', category: 'essential', description: 'Smooth overlap fade between slides' },
  { type: 'cut', label: 'Cut', emoji: '⚡', category: 'essential', description: 'Instant switch with no animation' },
  { type: 'fade-black', label: 'Fade Black', emoji: '◼', category: 'essential', description: 'Fade through black' },
  // Content-Aware
  { type: 'content-exit', label: 'Exit + Enter', emoji: '↗', category: 'content-aware', description: 'Elements exit then new slide enters' },
  { type: 'stagger-cascade', label: 'Cascade', emoji: '≋', category: 'content-aware', description: 'Elements cascade out sequentially' },
  { type: 'stagger-random', label: 'Random', emoji: '✧', category: 'content-aware', description: 'Elements exit in random order' },
  // Directional
  { type: 'slide-left', label: 'Slide Left', emoji: '←', category: 'directional', description: 'Slides move left' },
  { type: 'slide-right', label: 'Slide Right', emoji: '→', category: 'directional', description: 'Slides move right' },
  { type: 'slide-up', label: 'Slide Up', emoji: '↑', category: 'directional', description: 'Slides move up' },
  // Reveal
  { type: 'wipe-left', label: 'Wipe Left', emoji: '▸', category: 'reveal', description: 'Reveal from right to left' },
  { type: 'wipe-right', label: 'Wipe Right', emoji: '◂', category: 'reveal', description: 'Reveal from left to right' },
  { type: 'circle-reveal', label: 'Circle', emoji: '◉', category: 'reveal', description: 'Circular reveal from center' },
  // Emphasis
  { type: 'zoom-in', label: 'Zoom In', emoji: '⊕', category: 'emphasis', description: 'Zoom into next slide' },
  { type: 'zoom-out', label: 'Zoom Out', emoji: '⊖', category: 'emphasis', description: 'Zoom out from current slide' },
  { type: 'blur-fade', label: 'Blur Fade', emoji: '◌', category: 'emphasis', description: 'Blur and fade combination' },
  // Cinematic (v2)
  { type: 'flash-white', label: 'Flash White', emoji: '✸', category: 'cinematic', description: 'Light floods the frame, cut hidden under the peak' },
  { type: 'flash-accent', label: 'Flash Accent', emoji: '✹', category: 'cinematic', description: 'Brand-color flash cut (uses transition color)' },
  { type: 'whip-left', label: 'Whip Left', emoji: '⇜', category: 'cinematic', description: 'Fast lateral whip with motion smear' },
  { type: 'whip-right', label: 'Whip Right', emoji: '⇝', category: 'cinematic', description: 'Fast lateral whip with motion smear' },
  { type: 'glint-sweep', label: 'Glint Sweep', emoji: '☄', category: 'cinematic', description: 'Light strip races across, swap under its center' },
  { type: 'letterbox-pinch', label: 'Letterbox', emoji: '▬', category: 'cinematic', description: 'Cinema bars slam shut, cut inside, release' },
  { type: 'iris-close', label: 'Iris', emoji: '◎', category: 'cinematic', description: 'Iris collapses then blooms open on the next slide' },
  { type: 'slats-wipe', label: 'Slats', emoji: '▥', category: 'cinematic', description: 'Staggered bars cover and peel away' },
  { type: 'glitch-cut', label: 'Glitch', emoji: '⚡', category: 'cinematic', description: 'Digital corruption straddling the cut' },
  { type: 'film-dissolve', label: 'Film Dissolve', emoji: '✻', category: 'cinematic', description: 'Grainy dissolve — soft passage of time' },
  { type: 'wipe-up', label: 'Wipe Up', emoji: '▴', category: 'cinematic', description: 'Reveal upward' },
  { type: 'wipe-down', label: 'Wipe Down', emoji: '▾', category: 'cinematic', description: 'Reveal downward' },
];

/**
 * Get transition options grouped by category
 */
export function getTransitionsByCategory(): Record<TransitionCategory, TransitionOption[]> {
  return TRANSITION_OPTIONS.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {} as Record<TransitionCategory, TransitionOption[]>);
}

/**
 * Get a transition option by type
 */
export function getTransitionOption(type: TransitionType): TransitionOption | undefined {
  return TRANSITION_OPTIONS.find(opt => opt.type === type);
}

/**
 * Category display labels
 */
export const TRANSITION_CATEGORY_LABELS: Record<TransitionCategory, string> = {
  'essential': 'Essential',
  'content-aware': 'Content-Aware',
  'directional': 'Directional',
  'reveal': 'Reveal',
  'emphasis': 'Emphasis',
  'cinematic': 'Cinematic',
};

/**
 * Duration presets for transition selector
 */
export const TRANSITION_DURATION_PRESETS = [
  { value: 0.3, label: '0.3s (Fast)' },
  { value: 0.5, label: '0.5s (Normal)' },
  { value: 0.8, label: '0.8s (Slow)' },
  { value: 1.2, label: '1.2s (Dramatic)' },
] as const;

/**
 * Easing presets for transition selector
 */
export const TRANSITION_EASING_PRESETS = [
  { value: 'power2.inOut', label: 'Smooth' },
  { value: 'power3.inOut', label: 'Strong' },
  { value: 'expo.inOut', label: 'Expo' },
  { value: 'linear', label: 'Linear' },
] as const;

/**
 * Transition configuration
 */
export interface TransitionConfig {
  type: TransitionType;
  duration: number;  // Duration in seconds
  easing?: string;   // GSAP easing function (e.g., 'power2.inOut')
  /**
   * Resolved hex for color-carrying transitions (flash-accent, iris-close
   * backing, slats-wipe bars). Set by the planner per the pack's color role
   * (hero for brand punctuation, next slide's bg for continuity). Omitted =
   * neutral defaults (white flash, black iris, ink slats).
   */
  color?: string;
}

/**
 * Audio configuration for a slide
 */
export interface SlideAudioConfig {
  /** URL to the audio file */
  url?: string;
  /** Duration of the audio in seconds */
  duration?: number;
  /** Whether audio defines the slide duration (overrides animation duration) */
  isTimingSource?: boolean;
  /** Offset to start audio playback (seconds) */
  startOffset?: number;
  /** Volume level (0-1) */
  volume?: number;
}

/**
 * A single slide in the video manifest
 */
export interface ManifestSlide {
  /** Unique slide identifier */
  id: string;
  /** Display title */
  title: string;
  /** Path to slide JSON (for GsapSlidePreviewEnhanced) */
  jsonPath: string;
  /** Immutable workspace-CDN bake of this slide version (cookie-auth). Player prefers it; jsonPath is the fallback. */
  cdnJsonPath?: string;
  /** Child conversation ID (for video mode slides) */
  childConversationId?: string;
  /** Slide duration in seconds (from animation or audio) */
  duration: number;
  /** Transition to next slide */
  transitionOut?: TransitionConfig;
  /** Slide audio configuration */
  audio?: SlideAudioConfig;
  /** Thumbnail URL for filmstrip */
  thumbnail?: string;
  /** Computed: start time in the video timeline */
  startTime?: number;
  /** Computed: end time in the video timeline */
  endTime?: number;
  /** Audio range in master audio (audio-first mode) */
  audioRange?: { startTime: number; endTime: number };
}

/**
 * Master narration configuration (audio-first mode)
 * A single continuous audio that plays at the video level
 */
export interface MasterNarrationConfig {
  /** Signed URL to master audio file */
  url: string;
  /** Persistent GCS path for re-signing */
  gcsPath: string;
  /** Total duration in seconds */
  duration: number;
  /** Volume level (0-1), default 1.0 */
  volume: number;
}

/**
 * Background music configuration
 */
export interface BackgroundMusicConfig {
  /** URL to the audio file */
  url: string;
  /** Volume level (0-1) */
  volume: number;
  /** Whether to loop the audio */
  loop: boolean;
  /** Fade in duration (seconds) */
  fadeIn?: number;
  /** Fade out duration (seconds) */
  fadeOut?: number;
}

/**
 * Audio mix configuration (video-level audio settings)
 */
export interface AudioMixConfig {
  /** Narrator volume level (0-1), default 1.0 */
  narratorVolume: number;
}

/**
 * Video manifest metadata
 */
export interface VideoManifestMetadata {
  /** Total computed duration (including transitions) */
  totalDuration: number;
  /** Number of slides */
  slideCount: number;
  /** Creation timestamp */
  createdAt?: string;
  /** Last modified timestamp */
  updatedAt?: string;
  /** Video title */
  title?: string;
  /** Video description */
  description?: string;
  /** Project resolution. Defaults to 1920x1080 (16:9) if absent. */
  resolution?: { width: number; height: number };
}

/** A single declared URL-personalization parameter (see the URL personalization framework). */
export interface LaunchParamDecl {
  /** Slot key bound via `?p.<key>=` and `data-param*="<key>"`. */
  key: string;
  /** Value kind — advisory for tooling; the binder infers behavior from the attribute. */
  type?: 'text' | 'url' | 'number' | 'percent';
  /** Binding targets discovered at bake time, `"slideId:elementId.attr"`. */
  binds?: string[];
  /** For url slots: the authored fallback href's host(s) — the binder's default
   *  redirect allowlist, recorded at bake time so ?preflight=1 validates with
   *  the same rule the runtime applies. */
  authoredHosts?: string[];
  /** Whether a provider must supply this (advisory; never blocks playback). */
  required?: boolean;
  /** Human description surfaced in the provider integration doc / template URL. */
  description?: string;
}

/** Per-share URL-personalization schema, baked into the manifest and self-documenting. */
export interface LaunchSchema {
  /** Declared params (bake-time slide scan, enriched by the outline personalization block). */
  params: LaunchParamDecl[];
  /** Hostnames a `data-param-href` may point at (enforced by the binder). */
  allowedRedirectDomains?: string[];
  /** When true, params are dropped unless a valid HMAC `sig` is present (Phase 3). */
  requireSignature?: boolean;
}

/** vplay meta block on the baked top-level (shell) manifest. */
export interface VplayMeta {
  version: number;
  variant: string;
  fontFaces: string[];
  /** URL-personalization launch schema (absent when the share declares no slots). */
  launch?: LaunchSchema;
}

/**
 * Complete video manifest
 */
export interface VideoManifest {
  /** Version for future compatibility */
  version: '1.0';
  /** Ordered list of slides */
  slides: ManifestSlide[];
  /** Background music (video-level, optional) */
  backgroundMusic?: BackgroundMusicConfig;
  /** Audio mix settings (narrator volume, etc.) */
  audioMix?: AudioMixConfig;
  /** Master narration audio (audio-first mode - continuous audio at video level) */
  masterNarration?: MasterNarrationConfig;
  /** Default transition for slides without explicit transition */
  defaultTransition: TransitionConfig;
  /** Stage background shown behind slides during transitions (eliminates white flash) */
  stageBackground?: { type: 'image' | 'color'; url?: string; value?: string };
  /**
   * Program mode (docs/program-mode.md): one media clock, persistent layers and
   * authored seams over the slides. Opaque to the UI — the player runs it.
   */
  program?: Record<string, unknown>;
  /** Computed metadata */
  metadata: VideoManifestMetadata;
  /** vplay shell meta (version/variant/fontFaces/launch) — present on the baked top-level manifest. */
  vplay?: VplayMeta;
}

/**
 * Timeline segment for UI display
 */
export interface TimelineSegment {
  id: string;
  title: string;
  startTime: number;
  endTime: number;
  duration: number;
  /** Percentage of total duration (0-100) */
  widthPercent: number;
  /** Whether this segment is currently active */
  isActive?: boolean;
  /** Status for rendering (loading, error, etc.) */
  status?: 'pending' | 'generating' | 'completed' | 'failed';
}

/**
 * Video playback state
 */
export interface VideoPlaybackState {
  /** Whether video is currently playing */
  isPlaying: boolean;
  /** Current playback time in seconds */
  currentTime: number;
  /** Total video duration in seconds */
  totalDuration: number;
  /** Currently active slide ID */
  currentSlideId: string;
  /** Index of current slide (0-based) */
  currentSlideIndex: number;
  /** Time within current slide */
  currentSlideTime: number;
  /** Current slide duration */
  currentSlideDuration: number;
  /** Playback rate (1 = normal) */
  playbackRate: number;
  /** Whether we're in continuous playback mode (auto-advance) */
  isContinuousMode: boolean;
  /** Whether we're currently transitioning between slides */
  isTransitioning: boolean;
  /** Whether video was paused because user switched away from the tab */
  pausedByVisibility?: boolean;
  /**
   * DISPLAY (narration) clock — what a human should read. Interactive slides
   * carry a padded engine duration (the 120s hold ceiling) and gate windows the
   * viewer never hears on a clean run; the raw fields above include all of that,
   * so raw elapsed/total both jump and over-report. These fields count only the
   * narration actually spoken, are continuous across gates AND slide boundaries,
   * and self-correct per slide as slides load. Absent on older engine builds —
   * always fall back to the raw fields.
   */
  displayTime?: number;
  displayDuration?: number;
  displaySlideTime?: number;
  displaySlideDuration?: number;
  /** Per-slide display durations (index-aligned with manifest.slides). */
  displaySlideDurations?: number[];
  /** Per-slide display start offsets on the display axis. */
  displaySlideStarts?: number[];
  /**
   * Why this state changed, stamped by the controller so analytics can tell a
   * user action from framework churn (slide transitions move the playhead).
   * 'tick' = normal playback; absent on older controller builds.
   */
  changeSource?:
    | 'tick'
    | 'user_play'
    | 'user_pause'
    | 'user_seek'
    | 'user_scrub'
    | 'user_nav'
    | 'auto_advance'
    | 'transition';
}

/**
 * Default values for video manifest
 */
export const VIDEO_MANIFEST_DEFAULTS = {
  /** Default slide duration when not specified (seconds) */
  defaultSlideDuration: 5,
  /** Default transition (wipe-left) */
  defaultTransition: {
    type: 'wipe-left' as TransitionType,
    duration: 0.5,
    easing: 'power2.inOut',
  },
  /** Default audio volume */
  defaultAudioVolume: 1.0,
  /** Default background music volume */
  defaultBgMusicVolume: 0.3,
} as const;

/**
 * Slide data from the workspace (input for manifest builder)
 */
export interface WorkspaceSlide {
  id: string;
  title: string;
  childConversationId?: string;
  thumbnail?: string;
  duration?: number;
  audioUrl?: string;
  audioDuration?: number;
  status?: 'pending' | 'generating' | 'completed' | 'failed';
}

/**
 * Build a video manifest from workspace slides
 * Computes timeline positions (startTime, endTime) for each slide
 */
export function buildVideoManifest(
  slides: WorkspaceSlide[],
  options?: {
    title?: string;
    defaultTransition?: TransitionConfig;
    baseUrl?: string;
  }
): VideoManifest {
  const defaultTransition = options?.defaultTransition || VIDEO_MANIFEST_DEFAULTS.defaultTransition;
  const baseUrl = options?.baseUrl || '';

  // Build manifest slides with timing
  const manifestSlides: ManifestSlide[] = [];
  let currentTime = 0;

  for (let i = 0; i < slides.length; i++) {
    const slide = slides[i];
    const isLastSlide = i === slides.length - 1;

    // Determine slide duration (audio duration > explicit duration > default)
    const duration = slide.audioDuration || slide.duration || VIDEO_MANIFEST_DEFAULTS.defaultSlideDuration;

    const manifestSlide: ManifestSlide = {
      id: slide.id,
      title: slide.title,
      // Use correct API route: /api/conversations/:conversationId/slides/:slideId/serve
      jsonPath: slide.childConversationId
        ? `${baseUrl}/api/conversations/${slide.childConversationId}/slides/slide-001/serve`
        : '',
      childConversationId: slide.childConversationId,
      duration,
      startTime: currentTime,
      endTime: currentTime + duration,
      thumbnail: slide.thumbnail,
    };

    // Add audio config if available
    if (slide.audioUrl) {
      manifestSlide.audio = {
        url: slide.audioUrl,
        duration: slide.audioDuration,
        volume: VIDEO_MANIFEST_DEFAULTS.defaultAudioVolume,
      };
    }

    // Add transition (except for last slide)
    if (!isLastSlide) {
      manifestSlide.transitionOut = { ...defaultTransition };
    }

    manifestSlides.push(manifestSlide);

    // Update current time: slide duration + transition duration
    // Note: endTime is guaranteed to be set above
    currentTime = manifestSlide.endTime!;
    if (manifestSlide.transitionOut) {
      currentTime += manifestSlide.transitionOut.duration;
    }
  }

  return {
    version: '1.0',
    slides: manifestSlides,
    defaultTransition,
    metadata: {
      totalDuration: currentTime,
      slideCount: slides.length,
      title: options?.title,
      createdAt: new Date().toISOString(),
    },
  };
}

/**
 * Compute timeline segments for UI display
 */
export function computeTimelineSegments(manifest: VideoManifest): TimelineSegment[] {
  const segments: TimelineSegment[] = [];
  const totalDuration = manifest.metadata.totalDuration || 1;

  for (const slide of manifest.slides) {
    const startTime = slide.startTime || 0;
    const endTime = slide.endTime || startTime + slide.duration;
    const duration = endTime - startTime;

    segments.push({
      id: slide.id,
      title: slide.title,
      startTime,
      endTime,
      duration,
      widthPercent: (duration / totalDuration) * 100,
    });

    // Add transition segment if exists
    if (slide.transitionOut) {
      const transitionStart = endTime;
      const transitionEnd = transitionStart + slide.transitionOut.duration;

      segments.push({
        id: `${slide.id}-transition`,
        title: 'Transition',
        startTime: transitionStart,
        endTime: transitionEnd,
        duration: slide.transitionOut.duration,
        widthPercent: (slide.transitionOut.duration / totalDuration) * 100,
      });
    }
  }

  return segments;
}
